import { type FC } from 'react';

import {
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiMoreVertical } from 'react-icons/fi';

import { AddOrEditCandidateModal } from '@/features/admin-interview';
import {
  type CandidateBasicDto,
  InterviewStatus,
  type SurveyParticipationBasicDto,
} from '@/services/api';

import { DeleteModal } from '../../components/DeleteModal';
import {
  IconDelete,
  IconEditPencilOutline,
  IconPaperPlaneOutline,
} from '../../components/Icons';
import { PopoverItemRow } from '../../components/Popover';
import { useDeleteSurveyParticipantsMutation } from '../admin-survey/useAdminSurveyQueries';
import { ReInterviewModal } from './ReInterviewModal';
import { useRemoveCandidateMutation } from './useCandidateTableMutation';

type CandidateOptionsProps = {
  interviewId?: string;
  candidate: CandidateBasicDto | SurveyParticipationBasicDto;
};

export const CandidateOptions: FC<CandidateOptionsProps> = ({
  interviewId,
  candidate,
}) => {
  const popover = useDisclosure();
  const deleteModalDisclosure = useDisclosure();
  const editCandidateModalDisclosure = useDisclosure();
  const reInterviewModalDisclosure = useDisclosure();
  const { t } = useTranslation(['common', 'interview', 'survey']);

  const removeCandidateMutation = useRemoveCandidateMutation();
  const deleteSurveyParticipantsMutation =
    useDeleteSurveyParticipantsMutation();

  // Type guards
  const isCandidate = (
    data: CandidateBasicDto | SurveyParticipationBasicDto
  ): data is CandidateBasicDto => {
    return 'interviewId' in data;
  };

  const isSurveyParticipant = (
    data: CandidateBasicDto | SurveyParticipationBasicDto
  ): data is SurveyParticipationBasicDto => {
    return 'surveyId' in data;
  };

  return (
    <>
      <Popover
        closeOnEsc
        closeOnBlur
        placement="bottom-start"
        returnFocusOnClose={false}
        isOpen={popover.isOpen}
        onClose={popover.onClose}
      >
        <PopoverAnchor>
          <IconButton
            aria-label="More options"
            icon={<FiMoreVertical fontSize={20} color="gray.500" />}
            variant="ghost"
            size="sm"
            borderRadius="full"
            onClick={popover.onToggle}
            flexShrink={0}
            aspectRatio={1}
          />
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            w="250px"
            borderRadius="xl"
            borderColor="gray.100"
            shadow="md"
          >
            <PopoverBody p={0}>
              {isCandidate(candidate) &&
                candidate.status !== InterviewStatus.Invited &&
                candidate.status !== InterviewStatus.Added && (
                  <PopoverItemRow
                    name={t('common:actions.re-interview')}
                    icon={
                      <IconPaperPlaneOutline
                        color="gray.500"
                        fontSize={20}
                        mr={2}
                      />
                    }
                    onRowClick={reInterviewModalDisclosure.onOpen}
                    showArrow={false}
                  />
                )}

              {isCandidate(candidate) && (
                <PopoverItemRow
                  name={t('common:actions.edit')}
                  icon={
                    <IconEditPencilOutline
                      color="gray.500"
                      fontSize={20}
                      mr={2}
                    />
                  }
                  onRowClick={editCandidateModalDisclosure.onOpen}
                  showArrow={false}
                />
              )}

              <PopoverItemRow
                name={t('common:actions.delete')}
                icon={<IconDelete color="error.500" fontSize={20} mr={2} />}
                onRowClick={deleteModalDisclosure.onOpen}
                showArrow={false}
                textProps={{ color: 'error.500' }}
              />
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>

      {isCandidate(candidate) &&
        candidate.status !== InterviewStatus.Invited &&
        candidate.status !== InterviewStatus.Added && (
          <ReInterviewModal
            isOpen={reInterviewModalDisclosure.isOpen}
            onClose={reInterviewModalDisclosure.onClose}
            candidate={candidate}
            interviewId={interviewId!}
          />
        )}

      {isCandidate(candidate) && interviewId && (
        <AddOrEditCandidateModal
          isOpen={editCandidateModalDisclosure.isOpen}
          onClose={editCandidateModalDisclosure.onClose}
          interviewId={interviewId}
          isEdit
          candidate={candidate}
          editingCandidateInfo={{
            email: candidate.email ?? '',
            phoneNumber: candidate.phone,
            fullName: `${candidate.name ?? ''} ${candidate.surname ?? ''}`,
          }}
        />
      )}

      <DeleteModal
        isOpen={deleteModalDisclosure.isOpen}
        onClose={deleteModalDisclosure.onClose}
        title={
          isCandidate(candidate)
            ? t('interview:videoInterview.deleteCandidateTitle')
            : t('survey:deleteParticipantTitle')
        }
        subTitle={
          isCandidate(candidate)
            ? t('interview:videoInterview.deleteCandidateSubTitle')
            : t('survey:deleteParticipantSubTitle')
        }
        isLoading={
          isCandidate(candidate)
            ? removeCandidateMutation.isPending
            : deleteSurveyParticipantsMutation.isPending
        }
        onDelete={async () => {
          if (isCandidate(candidate) && interviewId) {
            await removeCandidateMutation.mutateAsync({
              interviewsId: interviewId,
              candidateIds: [candidate.id],
            });
          } else if (isSurveyParticipant(candidate)) {
            await deleteSurveyParticipantsMutation.mutateAsync({
              surveyId: candidate.surveyId!,
              participationIds: [candidate.id!],
            });
          }
        }}
      />
    </>
  );
};
